import React, {
  ComponentType,
  useState,
  useEffect,
  useCallback,
  ReactElement,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/system';
import { AppIcon } from '@smartfm/container/components/AppIcon/AppIcon';
import { useClientId } from '@smartfm/container/hooks/useClientId';
import { useBuildingIds } from '@smartfm/container/hooks/useBuildingIds';
import {
  useProjects,
  getAvailableAppsForClient,
  GetProjectsResponse,
  App,
} from '@smartfm/container/hooks/useProjects';
import {
  GetUserPermissionsResponse,
  useUserPermissions,
} from '@/hooks/useUserPermissions';
import { constructLink, getUniqueObjects } from '@/utils/functions';
import CircularProgress from '@mui/material/CircularProgress';
import { Stack } from '@smartfm/ui/components/Stack';

// User Props
export interface UserProps {
  firstName?: string;
  lastName?: string;
  email?: string;
  displayName?: string;
  id_token?: string;
}

interface IEmeraldSideNavItem {
  label: string;
  link: string;
  linkTarget?: string;
  linkComponent?: ComponentType<any>;
  icon?: string;
  iconComponent?: ReactElement;
  children?: IEmeraldSideNavItem[];
  separator?: boolean;
  active?: boolean;
  disabled?: boolean;
  onClick?: (event: MouseEvent) => void;
}

interface SideNavigationProps {
  isExpanded?: boolean;
  toggleNav?: () => void;
  setSelectedItem?: (item: string | null) => void;
  isPinned?: boolean;
  setIsPinned?: (isPinned: boolean) => void;
  handleDrawerToggle?: () => void;
  userInfo?: UserProps;
}

// Constants
const DRAWER_WIDTH = 350;
const COLLAPSED_WIDTH = 60;

// Simple storage helper
const saveToStorage = (key: string, value: boolean): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    console.warn(`Could not save to localStorage: ${key}`);
  }
};

// Styled components
const NavDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    boxShadow: '0px 3px 8px 0px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 48px)',
    justifyContent: 'space-between',
    overflow: 'hidden',
    top: '48px',
    transition: 'width 0.3s',
    zIndex: 999,
  },
});

const NavItem = styled(ListItem, {
  shouldForwardProp: prop => prop !== 'active',
})<{ active: boolean; separator: boolean }>(({ active, separator }) => ({
  '&::before': {
    backgroundColor: active ? '#003f2d' : 'transparent',
    bottom: 0,
    content: '""',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '6px',
  },
  '&:hover': {
    backgroundColor: 'rgba(0, 63, 45, 0.08)',
    cursor: 'pointer',
  },
  backgroundColor: active ? 'rgba(0, 63, 45, 0.12)' : 'inherit',
  borderTop: separator ? '1px solid var(--eds-sn-color-border)' : 'inherit',
  padding: '16px',
  position: 'relative',
}));

const NavFooter = styled('div')(() => ({
  backgroundColor: 'white',
  borderTop: '1px solid rgba(0, 63, 45, 0.1)',
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '12px',
}));

const PIN_KEY = 'nav-pinned';

const SideNavigation: React.FC<SideNavigationProps> = () => {
  const [clientId] = useClientId();
  const [_, buildings] = useBuildingIds();
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState<boolean>(
    () => localStorage.getItem(PIN_KEY) === 'true'
  );

  const location = useLocation();
  const navigate = useNavigate();

  const userPermissionsQuery = useUserPermissions(
    useCallback((data: GetUserPermissionsResponse) => {
      if (data.isAdmin) {
        localStorage.setItem('is-admin', JSON.stringify(+data.isAdmin));
      } else {
        localStorage.removeItem('is-admin');
      }
      return data;
    }, [])
  );

  const { data, isLoading } = useProjects(
    useCallback(
      (data: GetProjectsResponse): IEmeraldSideNavItem[] => {
        try {
          let availableApps: App[] = [];

          if (clientId) {
            availableApps = getAvailableAppsForClient(data, clientId);
          } else {
            const duplicatedApps =
              data.data?.map(client => client.apps).flat() ?? [];
            availableApps = getUniqueObjects(duplicatedApps, 'application_id');
          }

          const appsData: IEmeraldSideNavItem[] = availableApps.map(app => {
            const link = `/${app.nav_path}`;

            return {
              iconComponent: <AppIcon icon={app.icon_url} />,
              label: app.application_name,
              link,
            };
          });
          appsData.sort((a, b) => a.label.localeCompare(b.label));

          // Dashboard
          const dashboardLink = location.pathname.startsWith('/demo')
            ? '/demo'
            : '/';

          appsData.splice(0, 0, {
            iconComponent: <AppIcon icon={'dashboard'} />,
            label: 'Dashboard',
            link: dashboardLink,
          });

          // Admin
          if (
            userPermissionsQuery.data &&
            userPermissionsQuery.data.isAdmin === true
          ) {
            appsData.push({
              iconComponent: <AppIcon icon={'admin_panel_settings'} />,
              label: 'Administration',
              link: '/ui/admin',
              separator: true,
            });
          }

          return appsData;
        } catch (e) {
          console.error(e);
          return [];
        }
      },
      [clientId, userPermissionsQuery.data]
    )
  );
  // Check if a path is active
  const isPathActive = (path: string): boolean => {
    return path === location.pathname;
  };

  // Save pinned state to localStorage when it changes
  useEffect(() => {
    saveToStorage(PIN_KEY, isPinned);
  }, [isPinned]);

  // Calculate drawer width based on state
  const getDrawerWidth = () => {
    if (isPinned || isHovered) return DRAWER_WIDTH;
    return COLLAPSED_WIDTH;
  };

  // Handle menu item click
  const handleItemClick = (item: IEmeraldSideNavItem) => {
    navigate(constructLink(item.link, clientId, buildings));
  };

  // Toggle pin state
  const togglePin = () => {
    if (setIsPinned) {
      setIsPinned(!isPinned);
    }
  };

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isInside = !!target.closest('#sidebar');

      if (!isPinned) {
        if (isHovered !== isInside) {
          setIsHovered(isInside);
        }
      }
    }

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isPinned, isHovered]);

  // Render a menu item and its children recursively
  const renderMenuItem = (item: IEmeraldSideNavItem) => {
    // Determine if this item is active
    const isActive = isPathActive(item.link);
    return (
      <React.Fragment key={item.link}>
        <NavItem
          separator={Boolean(item.separator)}
          active={isActive}
          onClick={() => handleItemClick(item)}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              ...(isActive ? { color: 'var(--eds-sn-color-active)' } : {}),
            }}
          >
            {item.iconComponent}
          </ListItemIcon>
          {(isPinned || isHovered) && (
            <ListItemText
              primary={item.label}
              sx={{
                margin: 0,
                whiteSpace: 'nowrap',
              }}
            />
          )}
        </NavItem>
      </React.Fragment>
    );
  };

  const Content = () => {
    if (isLoading) {
      return (
        <Stack
          justifyContent='center'
          alignItems='center'
          sx={{
            backgroundColor: 'white',
            height: '100%',
          }}
        >
          <CircularProgress size='1rem' />
        </Stack>
      );
    }
    if (data) {
      return (
        <List
          disablePadding
          sx={{ marginBottom: '56px' }}
        >
          {data.map(item => renderMenuItem(item))}
        </List>
      );
    }
    return null;
  };

  return (
    <NavDrawer
      id='sidebar'
      variant='permanent'
      open
      sx={{
        '& .MuiDrawer-paper': {
          width: getDrawerWidth(),
        },
        flexShrink: 0,
        tabIndex: 0,
        top: '48px',
        transition: '0.5s',
        width: isPinned ? DRAWER_WIDTH : COLLAPSED_WIDTH,
      }}
      hideBackdrop={true}
    >
      <Content />
      {/* Footer with pin button or user info */}
      <NavFooter>
        <Tooltip
          title={isPinned ? 'Unpin navigation' : 'Pin navigation'}
          arrow
        >
          <IconButton onClick={togglePin}>
            <i
              className='emerald-icon pin-icon material-symbols-outlined'
              aria-hidden='false'
              tabIndex={0}
            >
              {isPinned ? 'keep_off' : 'keep'}
            </i>
          </IconButton>
        </Tooltip>
      </NavFooter>
    </NavDrawer>
  );
};

export default SideNavigation;
